import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { lazyPage } from "./lib/lazyPage";
import AppToaster from "./components/AppToaster";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ImpersonationBanner from "./components/ImpersonationBanner";
import AppDownloadRibbon from "./components/AppDownloadRibbon";
import ForceUpdateGate from "./components/ForceUpdateGate";
import ProtectedRoute from "./components/ProtectedRoute";
import OwnerOnlyRoute from "./components/OwnerOnlyRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import AuthCallback from "./pages/AuthCallback";
import Landing from "./pages/Landing";
import AuthPage from "./pages/AuthPage";
import NotFound from "./pages/NotFound";
import PublicActionPage from "./pages/PublicActionPage";
import PublicCreatorDirectory from "./pages/PublicCreatorDirectory";
import Storefront, { SellPage } from "./pages/Storefront";
import QRRedirectShim from "./pages/QRRedirectShim";
import Legal from "./pages/Legal";
import { Navigate } from "react-router-dom";

// v149.64 — Only the public front door (landing, bio pages, sign-in,
// storefront, product page, QR redirect) is in the first download; every
// other page is a `lazyPage`, fetched the first time its route opens.
const AppShell = lazyPage(() => import("./components/AppShell"));
const AdminShell = lazyPage(() => import("./components/AdminShell"));
const InstagramCallback = lazyPage(() => import("./pages/InstagramCallback"));
const VerticalLanding = lazyPage(() => import("./pages/VerticalLanding"));
const TrendingPages = lazyPage(() => import("./pages/TrendingPages"));
const ResetPassword = lazyPage(() => import("./pages/ResetPassword"));
const Onboarding = lazyPage(() => import("./pages/Onboarding"));
const Dashboard = lazyPage(() => import("./pages/Dashboard"));
const Analyzer = lazyPage(() => import("./pages/Analyzer"));
const Hashtags = lazyPage(() => import("./pages/Hashtags"));
const Weekly = lazyPage(() => import("./pages/Weekly"));
const Settings = lazyPage(() => import("./pages/Settings"));
const AccountSettings = lazyPage(() => import("./pages/AccountSettings"));
const ProfileDetails = lazyPage(() => import("./pages/ProfileDetails"));
const Upgrade = lazyPage(() => import("./pages/Upgrade"));
const Posts = lazyPage(() => import("./pages/Posts"));
const HashtagPerformance = lazyPage(() => import("./pages/HashtagPerformance"));
const AiCoach = lazyPage(() => import("./pages/AiCoach"));
const ABTest = lazyPage(() => import("./pages/ABTest"));
const Velocity = lazyPage(() => import("./pages/Velocity"));
const Workspace = lazyPage(() => import("./pages/Workspace"));
const BrandBrief = lazyPage(() => import("./pages/BrandBrief"));
const Projects = lazyPage(() => import("./pages/Projects"));
const ProjectDetail = lazyPage(() => import("./pages/ProjectDetail"));
const Competitors = lazyPage(() => import("./pages/Competitors"));
const Ads = lazyPage(() => import("./pages/Ads"));
const YouTube = lazyPage(() => import("./pages/YouTube"));
const YoutubeCallback = lazyPage(() => import("./pages/YoutubeCallback"));
const ActionHub = lazyPage(() => import("./pages/ActionHub"));
const ActionHubBio = lazyPage(() => import("./pages/ActionHubBio"));
const ViewInApp = lazyPage(() => import("./pages/ViewInApp"));
const BioPageLanding = lazyPage(() => import("./pages/BioPageLanding"));
const ActionHubLoyalty = lazyPage(() => import("./pages/ActionHubLoyalty"));
const FollowSticker = lazyPage(() => import("./pages/FollowSticker"));
const MyLoyalty = lazyPage(() => import("./pages/MyLoyalty"));
const ActionHubEngagement = lazyPage(() => import("./pages/ActionHubEngagement"));
const ActionHubCommerce = lazyPage(() => import("./pages/ActionHubCommerce"));
const ActionHubAnalytics = lazyPage(() => import("./pages/ActionHubAnalytics"));
const ActionPageEditor = lazyPage(() => import("./pages/ActionPageEditor"));
const OrgHub = lazyPage(() => import("./pages/OrgHub"));
const BranchInvite = lazyPage(() => import("./pages/BranchInvite"));
const AIHistory = lazyPage(() => import("./pages/AIHistory"));
const BrandCampaigns = lazyPage(() => import("./pages/BrandCampaigns"));
const BrandProfile = lazyPage(() => import("./pages/BrandProfile"));
const CreatorEarnings = lazyPage(() => import("./pages/CreatorEarnings"));
const Marketplace = lazyPage(() => import("./pages/Marketplace"));
const MarketplaceCampaignDetail = lazyPage(() => import("./pages/MarketplaceCampaignDetail"));
const InfluencerDetail = lazyPage(() => import("./pages/InfluencerDetail"));
const InfluencerSettings = lazyPage(() => import("./pages/InfluencerSettings"));
const MarketplaceRequests = lazyPage(() => import("./pages/MarketplaceRequests"));
const Inbox = lazyPage(() => import("./pages/Inbox"));
const Feed = lazyPage(() => import("./pages/Feed"));
const WalletPage = lazyPage(() => import("./pages/Wallet"));
const AdminWallets = lazyPage(() => import("./pages/admin/AdminWallets"));
const AdminTransactions = lazyPage(() => import("./pages/admin/AdminTransactions"));
const AdminCouponMarket = lazyPage(() => import("./pages/admin/AdminCouponMarket"));
const AdminPayouts = lazyPage(() => import("./pages/AdminPayouts"));
const AdminWithdrawals = lazyPage(() => import("./pages/AdminWithdrawals"));
const AdminPromotionProofs = lazyPage(() => import("./pages/AdminPromotionProofs"));
const AcceptInvite = lazyPage(() => import("./pages/AcceptInvite"));
const ChooseAccount = lazyPage(() => import("./pages/ChooseAccount"));
const Join = lazyPage(() => import("./pages/Join"));
const PublicPassbook = lazyPage(() => import("./pages/PublicPassbook"));
const PublicPageAnalytics = lazyPage(() => import("./pages/PublicPageAnalytics"));
const AdminDashboard = lazyPage(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazyPage(() => import("./pages/admin/AdminUsers"));
const AdminEndUsers = lazyPage(() => import("./pages/admin/AdminEndUsers"));
const AdminStaff = lazyPage(() => import("./pages/admin/AdminStaff"));
const AdminPageStats = lazyPage(() => import("./pages/admin/AdminPageStats"));
const AdminPlans = lazyPage(() => import("./pages/admin/AdminPlans"));
const AdminSubscriptions = lazyPage(() => import("./pages/admin/AdminSubscriptions"));
const AdminSettings = lazyPage(() => import("./pages/admin/AdminSettings"));
const AdminSupportTickets = lazyPage(() => import("./pages/admin/AdminSupportTickets"));
const AdminCorporateLeads = lazyPage(() => import("./pages/admin/AdminCorporateLeads"));
const AdminFeatureFlags = lazyPage(() => import("./pages/admin/AdminFeatureFlags"));
const AdminBlockTiers = lazyPage(() => import("./pages/admin/AdminBlockTiers"));
const AdminLegal = lazyPage(() => import("./pages/admin/AdminLegal"));
const AdminLocations = lazyPage(() => import("./pages/admin/AdminLocations"));
const AdminLocationSuggestions = lazyPage(() => import("./pages/admin/AdminLocationSuggestions"));
const AdminReferrals = lazyPage(() => import("./pages/admin/AdminReferrals"));
const AdminAIUsage = lazyPage(() => import("./pages/admin/AdminAIUsage"));
const AdminAILimits = lazyPage(() => import("./pages/admin/AdminAILimits"));
const AdminAIMargins = lazyPage(() => import("./pages/admin/AdminAIMargins"));
const AdminAICoach = lazyPage(() => import("./pages/admin/AdminAICoach"));
const AdminLanding = lazyPage(() => import("./pages/admin/AdminLanding"));
const AdminLandingVideos = lazyPage(() => import("./pages/admin/AdminLandingVideos"));
const AdminSmartQR = lazyPage(() => import("./pages/admin/AdminSmartQR"));
const AdminSmartQRLifecycle = lazyPage(() => import("./pages/admin/AdminSmartQRLifecycle"));
const AdminSmartQRPlans = lazyPage(() => import("./pages/admin/AdminSmartQRPlans"));
const AdminReconcile = lazyPage(() => import("./pages/admin/AdminReconcile"));
const AdminQRSales = lazyPage(() => import("./pages/admin/AdminQRSales"));
const QRSalesDashboard = lazyPage(() => import("./pages/QRSalesDashboard"));
const AdminMessagingPricing = lazyPage(() => import("./pages/admin/AdminMessagingPricing"));
const AdminAppVersion = lazyPage(() => import("./pages/admin/AdminAppVersion"));
const TicketReceipt = lazyPage(() => import("./pages/TicketReceipt"));
const ScanPage = lazyPage(() => import("./pages/ScanPage"));
const EventLivePage = lazyPage(() => import("./pages/EventLivePage"));
const AdminWaNotifications = lazyPage(() => import("./pages/admin/AdminWaNotifications"));
const AdminWaBroadcasts = lazyPage(() => import("./pages/admin/AdminWaBroadcasts"));
const NotificationSettings = lazyPage(() => import("./pages/NotificationSettings"));
const AdminPlatformCommissions = lazyPage(() => import("./pages/admin/AdminPlatformCommissions"));
const AdminQuickPromote = lazyPage(() => import("./pages/admin/AdminQuickPromote"));
const AdminQuickPromoteAd = lazyPage(() => import("./pages/admin/AdminQuickPromoteAd"));
const AdminBusinessCategories = lazyPage(() => import("./pages/admin/AdminBusinessCategories"));
const QuickPromote = lazyPage(() => import("./pages/QuickPromote"));
const QuickPromoteDetail = lazyPage(() => import("./pages/QuickPromoteDetail"));
const AdminSiteAnalytics = lazyPage(() => import("./pages/admin/AdminSiteAnalytics"));
const AdminUsersOps = lazyPage(() => import("./pages/admin/AdminUsersOps"));
const AdminOtps = lazyPage(() => import("./pages/admin/AdminOtps"));
const AdminJsErrors = lazyPage(() => import("./pages/admin/AdminJsErrors"));
const Refer = lazyPage(() => import("./pages/Refer"));
const DataUse = lazyPage(() => import("./pages/DataUse"));
const DiagAuth = lazyPage(() => import("./pages/DiagAuth"));
const CouponMarketDashboard = lazyPage(() => import("./pages/CouponMarketInfluencer"), "CouponMarketDashboard");
const CouponMarketList = lazyPage(() => import("./pages/CouponMarketInfluencer"), "CouponMarketList");
const CouponMarketCreate = lazyPage(() => import("./pages/CouponMarketInfluencer"), "CouponMarketCreate");
const VoucherRedeem = lazyPage(() => import("./pages/VoucherRedeem"));
const VoucherMarketplace = lazyPage(() => import("./pages/VoucherMarketplace"));
const Receipt = lazyPage(() => import("./pages/Receipt"));
const MyStuff = lazyPage(() => import("./pages/MyStuff"));
const BookingsCalendar = lazyPage(() => import("./pages/BookingsCalendar"));
const Orders = lazyPage(() => import("./pages/Orders"));
const Customers = lazyPage(() => import("./pages/Customers"));
const CustomerHub = lazyPage(() => import("./pages/CustomerHub"));
const PageResultsList = lazyPage(() => import("./pages/PageResultsList"));
const AffiliateMarketplace = lazyPage(() => import("./pages/AffiliateMarketplace"));
const AffiliateEarnings = lazyPage(() => import("./pages/AffiliateEarnings"));
const PromotionHub = lazyPage(() => import("./pages/PromotionHub"));
const Coupons = lazyPage(() => import("./pages/Coupons"));
const ChildSafetyStandards = lazyPage(() => import("./pages/ChildSafetyStandards"));
const CouponPublicPreview = lazyPage(() => import("./pages/CouponMarketCustomer"), "CouponPublicPreview");
const CouponCustomerView = lazyPage(() => import("./pages/CouponMarketCustomer"), "CouponCustomerView");
const CouponRedeem = lazyPage(() => import("./pages/CouponMarketCustomer"), "CouponRedeem");
const CouponBusinessConfirm = lazyPage(() => import("./pages/CouponMarketCustomer"), "CouponBusinessConfirm");
const MySmartQRs = lazyPage(() => import("./pages/MySmartQRs"), "MySmartQRs");
const SmartQrAnalytics = lazyPage(() => import("./pages/MySmartQRs"), "SmartQrAnalytics");
// v149.74 — Smart Screens (host shop + advertiser detail + admin)
const SmartScreens = lazyPage(() => import("./pages/SmartScreens"));
const SmartScreenDetail = lazyPage(() => import("./pages/SmartScreens"), "SmartScreenDetail");
const ScreenBookingDetail = lazyPage(() => import("./pages/ScreenBookingDetail"));
const AdminSmartScreens = lazyPage(() => import("./pages/admin/AdminSmartScreens"));

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="overline">Loading…</p></div>;
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) return <Navigate to="/app" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  // v149.64 — A signed-in visitor is about to be sent to /app: fetch its
  // chunks now so the redirect doesn't wait on them.
  useEffect(() => {
    if (!user) return;
    AppShell.preload();
    Dashboard.preload();
    if (user.is_admin) AdminShell.preload();
  }, [user?.user_id, user?.is_admin]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<Landing />} />
      {/* v146.75 — SEO landing pages per business vertical. Dynamic
          `/for/:vertical` route accepts singular + plural aliases
          (doctors / doctor, hotels / hotel, restaurants / restaurant,
          etc.). Unknown verticals redirect to /. */}
      <Route path="/for/:vertical" element={<VerticalLanding />} />
      <Route path="/passbook/:token" element={<PublicPassbook />} />
      <Route path="/signin" element={<AuthPage />} />
      <Route path="/signup" element={<AuthPage />} />
      <Route path="/trending" element={<TrendingPages />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<Legal docType="terms" />} />
      <Route path="/privacy" element={<Legal docType="privacy" />} />
      {/* Linkola Player Android app — the privacy-policy URL given to Google Play. */}
      <Route path="/linkola-player/privacy" element={<Legal docType="player_privacy" />} />
      <Route path="/refund-policy" element={<Legal docType="refund" />} />
      <Route path="/refund" element={<Legal docType="refund" />} />
      <Route path="/about" element={<Legal docType="about" />} />
      <Route path="/about-us" element={<Legal docType="about" />} />
      <Route path="/pricing" element={<Legal docType="pricing" />} />
      <Route path="/contact" element={<Legal docType="contact" />} />
      <Route path="/contact-us" element={<Legal docType="contact" />} />
      <Route path="/legal/:type" element={<Legal />} />
      <Route path="/data-use" element={<DataUse />} />
      {/* v143.9 (Feb 2026) — Google Play Child Safety Standards page.
          MUST stay publicly reachable (no auth) at a stable URL. */}
      <Route path="/child-safety-standards" element={<ChildSafetyStandards />} />
      <Route path="/diag/auth" element={<DiagAuth />} />
      <Route path="/diag-auth" element={<DiagAuth />} />
      {/* v90 (Feb 2026) — Smart QR public shim: catches linkola.app/q/:code
          and forwards to /api/q/:code where the backend 302-redirects to
          the current destination page. */}
      <Route path="/q/:code" element={<QRRedirectShim />} />

      {/* v53 — Paid Coupon Marketplace public routes (no auth needed) */}
      <Route path="/coupon/:campaign_id" element={<CouponPublicPreview />} />
      <Route path="/coupons/:unique_code" element={<CouponCustomerView />} />
      <Route path="/coupons/:unique_code/redeem" element={<CouponRedeem />} />
      <Route path="/coupon-confirm/:token" element={<CouponBusinessConfirm />} />
      {/* Creator Action Hub — public landing pages */}
      <Route path="/u/:creator_slug" element={<PublicCreatorDirectory />} />
      <Route path="/store/:slug" element={<Storefront />} />
      <Route path="/p/:product_id" element={<SellPage />} />
      <Route path="/u/:creator_slug/:page_slug" element={<PublicActionPage />} />
      <Route path="/analytics/:token" element={<PublicPageAnalytics />} />
      {/* Public Marketplace of brand campaigns */}
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/marketplace/brands/:brandId" element={<BrandProfile />} />
      <Route path="/marketplace/influencers/:userId" element={<InfluencerDetail />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/branch-invite/:token" element={<BranchInvite />} />
      <Route path="/join" element={<Join />} />
      <Route path="/instagram/callback" element={<ProtectedRoute><InstagramCallback /></ProtectedRoute>} />
      <Route path="/youtube/callback" element={<YoutubeCallback />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/choose-account" element={<ProtectedRoute><ChooseAccount /></ProtectedRoute>} />
      <Route path="/qr-sales" element={<ProtectedRoute><QRSalesDashboard /></ProtectedRoute>} />
      <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route path="/app" element={<Dashboard />} />
        <Route path="/app/analyzer" element={<Analyzer />} />
        <Route path="/app/hashtags" element={<Hashtags />} />
        <Route path="/app/weekly" element={<Weekly />} />
        <Route path="/app/settings" element={<OwnerOnlyRoute label="Settings"><Settings /></OwnerOnlyRoute>} />
        <Route path="/app/settings/profile" element={<OwnerOnlyRoute label="Settings"><ProfileDetails /></OwnerOnlyRoute>} />
        <Route path="/app/settings/account" element={<OwnerOnlyRoute label="Settings"><AccountSettings /></OwnerOnlyRoute>} />
        <Route path="/app/settings/notifications" element={<OwnerOnlyRoute label="Settings"><NotificationSettings /></OwnerOnlyRoute>} />
        <Route path="/app/upgrade" element={<OwnerOnlyRoute label="Plans & billing"><Upgrade /></OwnerOnlyRoute>} />
        <Route path="/app/trending" element={<TrendingPages />} />
        <Route path="/app/workspace" element={<Workspace />} />
        <Route path="/app/posts" element={<Posts />} />
        <Route path="/app/hashtag-perf" element={<HashtagPerformance />} />
        <Route path="/app/coach" element={<AiCoach />} />
        <Route path="/app/abtest" element={<ABTest />} />
        <Route path="/app/velocity" element={<Velocity />} />
        <Route path="/app/brand" element={<BrandBrief />} />
        <Route path="/app/projects" element={<Projects />} />
        <Route path="/app/projects/:id" element={<ProjectDetail />} />
        <Route path="/app/competitors" element={<Competitors />} />
        <Route path="/app/ads" element={<Ads />} />
        <Route path="/app/youtube" element={<YouTube />} />
        <Route path="/app/action-hub" element={<ActionHub />} />
        <Route path="/app/action-hub/hub" element={<ActionHubBio />} />
        {/* v145.7 — In-app viewer for /u/{slug} and /p/{slug} so mobile
            + native WebView users get a "Back to app" button when they
            open a bio or action page from within the app shell.
            v147.42 — Split into two typed routes so ViewInApp can
            render the actual PublicCreatorDirectory / PublicActionPage
            component (via `useParams().creator_slug` + optional
            `page_slug`) INSTEAD of embedding the URL inside a
            sandboxed <iframe>. The iframe stripped WebView
            permissions needed for inline <video>, cross-origin
            images, and window.open() Google-Maps redirects on
            native iOS/Android — Trending→Bio was breaking those
            three exact features while direct /u/{slug} worked
            fine. */}
        <Route path="/app/view/u/:creator_slug" element={<ViewInApp />} />
        <Route path="/app/view/p/:creator_slug/:page_slug" element={<ViewInApp />} />
        {/* v145.10 — Bio Page mobile-nav tile lands here first; the
            component reads the signed-in user's slug and redirects to
            /app/view/u/{slug} so users see their PREVIEW by default,
            with a one-tap Edit button in the AppShell header to jump
            into the Designer. */}
        <Route path="/app/bio-page" element={<BioPageLanding />} />
        <Route path="/app/action-hub/loyalty" element={<ActionHubLoyalty />} />
        <Route path="/app/action-hub/follow-sticker" element={<FollowSticker />} />
        <Route path="/app/my-loyalty" element={<MyLoyalty />} />
        <Route path="/app/action-hub/engagement" element={<ActionHubEngagement />} />
        <Route path="/app/action-hub/commerce" element={<ActionHubCommerce />} />
        <Route path="/app/action-hub/analytics" element={<ActionHubAnalytics />} />
        <Route path="/app/action-hub/campaigns" element={<BrandCampaigns />} />
        <Route path="/app/action-hub/earnings" element={<CreatorEarnings />} />
        <Route path="/app/action-hub/bookings" element={<BookingsCalendar />} />
        <Route path="/app/action-hub/orders" element={<Orders />} />
        <Route path="/app/customers" element={<Customers />} />
        <Route path="/app/customer-hub" element={<CustomerHub />} />
        <Route path="/app/customer-hub/page-results" element={<PageResultsList />} />
        <Route path="/app/affiliate/marketplace" element={<AffiliateMarketplace />} />
        <Route path="/app/affiliate/earnings" element={<AffiliateEarnings />} />
        <Route path="/app/promotion-hub" element={<PromotionHub />} />
        <Route path="/app/action-hub/pages/:id" element={<ActionPageEditor />} />
        <Route path="/app/org" element={<OrgHub />} />
        <Route path="/app/marketplace/influencer-settings" element={<InfluencerSettings />} />
        <Route path="/app/marketplace/requests" element={<MarketplaceRequests />} />
        <Route path="/app/inbox" element={<Inbox />} />
        <Route path="/app/feed" element={<Feed />} />
        <Route path="/app/marketplace/campaigns/:campaign_id" element={<MarketplaceCampaignDetail />} />
        <Route path="/app/wallet" element={<OwnerOnlyRoute label="Wallet"><WalletPage /></OwnerOnlyRoute>} />
        {/* v149 — Quick Promote: local ads on nearby shops' bio pages */}
        <Route path="/app/promote" element={<QuickPromote />} />
        {/* v149.74 — a Smart Screens booking's own page (declared before :adId so it wins) */}
        <Route path="/app/promote/screens/:bookingId" element={<ScreenBookingDetail />} />
        <Route path="/app/promote/:adId" element={<QuickPromoteDetail />} />
        <Route path="/app/smart-screens" element={<SmartScreens />} />
        <Route path="/app/smart-screens/:screen_id" element={<SmartScreenDetail />} />
        <Route path="/app/refer" element={<Refer />} />

        {/* v53 — Paid Coupon Marketplace (influencer/brand surfaces) */}
        <Route path="/app/coupon-market" element={<CouponMarketDashboard />} />
        {/* v143.6 (Feb 2026) — end-user coupon surface (Discover + My Coupons) */}
        <Route path="/app/coupons" element={<Coupons />} />
        <Route path="/app/my-stuff" element={<MyStuff />} />
        <Route path="/app/coupon-market/campaigns" element={<CouponMarketList />} />
        <Route path="/app/coupon-market/campaigns/new" element={<CouponMarketCreate />} />
        <Route path="/app/coupon-market/campaigns/:campaign_id/edit" element={<CouponMarketCreate />} />
        <Route path="/app/coupon-market/redeem" element={<VoucherRedeem />} />
        <Route path="/app/coupon-market/marketplace" element={<VoucherMarketplace />} />
        <Route path="/app/receipt/:kind/:id" element={<Receipt />} />
        <Route path="/app/ai/history" element={<AIHistory />} />
        {/* v90 (Feb 2026) — user-side Smart QR management */}
        <Route path="/app/smart-qr" element={<MySmartQRs />} />
        <Route path="/app/smart-qr/:qr_id" element={<SmartQrAnalytics />} />
      </Route>
      <Route element={<AdminOnly><AdminShell /></AdminOnly>}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/endusers" element={<AdminEndUsers />} />
        <Route path="/admin/staff" element={<AdminStaff />} />
        <Route path="/admin/page-stats" element={<AdminPageStats />} />
        <Route path="/admin/plans" element={<AdminPlans />} />
        <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
        <Route path="/admin/support-tickets" element={<AdminSupportTickets />} />
        <Route path="/admin/corporate-leads" element={<AdminCorporateLeads />} />
        <Route path="/admin/feature-flags" element={<AdminFeatureFlags />} />
        <Route path="/admin/block-tiers" element={<AdminBlockTiers />} />
        <Route path="/admin/legal" element={<AdminLegal />} />
        <Route path="/admin/locations" element={<AdminLocations />} />
        <Route path="/admin/location-suggestions" element={<AdminLocationSuggestions />} />
        <Route path="/admin/referrals" element={<AdminReferrals />} />
        <Route path="/admin/coupon-market" element={<AdminCouponMarket />} />
        <Route path="/admin/wallets" element={<AdminWallets />} />
        <Route path="/admin/transactions" element={<AdminTransactions />} />
        <Route path="/admin/payouts" element={<AdminPayouts />} />
        <Route path="/admin/wallet-withdrawals" element={<AdminWithdrawals />} />
        <Route path="/admin/ai-usage" element={<AdminAIUsage />} />
        <Route path="/admin/ai-limits" element={<AdminAILimits />} />
        <Route path="/admin/ai-margins" element={<AdminAIMargins />} />
        <Route path="/admin/ai-coach" element={<AdminAICoach />} />
        <Route path="/admin/landing" element={<AdminLanding />} />
        <Route path="/admin/landing-videos" element={<AdminLandingVideos />} />
        <Route path="/admin/js-errors" element={<AdminJsErrors />} />
        <Route path="/admin/promotion-proofs" element={<AdminPromotionProofs />} />
        <Route path="/admin/smart-qr" element={<AdminSmartQR />} />
        <Route path="/admin/smart-qr-lifecycle" element={<AdminSmartQRLifecycle />} />
        <Route path="/admin/smart-qr-plans" element={<AdminSmartQRPlans />} />
        <Route path="/admin/smart-screens" element={<AdminSmartScreens />} />
        <Route path="/admin/reconcile" element={<AdminReconcile />} />
        <Route path="/admin/qr-sales" element={<AdminQRSales />} />
        <Route path="/admin/messaging-pricing" element={<AdminMessagingPricing />} />
        <Route path="/admin/app-version" element={<AdminAppVersion />} />
        <Route path="/admin/wa-notifications" element={<AdminWaNotifications />} />
        <Route path="/admin/wa-broadcasts" element={<AdminWaBroadcasts />} />
        <Route path="/admin/platform-commissions" element={<AdminPlatformCommissions />} />
        <Route path="/admin/quick-promote" element={<AdminQuickPromote />} />
        <Route path="/admin/quick-promote/ads/:adId" element={<AdminQuickPromoteAd />} />
        <Route path="/admin/business-categories" element={<AdminBusinessCategories />} />
        <Route path="/admin/site-analytics" element={<AdminSiteAnalytics />} />
        <Route path="/admin/users-ops" element={<AdminUsersOps />} />
        <Route path="/admin/otps" element={<AdminOtps />} />
      </Route>
      {/* v147.67 — Public / semi-public flows. `/tickets/:orderId` is
          the buyer-facing receipt (must be reachable without auth,
          otherwise emailed ticket links land on a 404). `/scan` is
          the door-scan app used by creators + event staff — auth is
          checked server-side by the check-in endpoint. These were
          previously nested INSIDE the AdminOnly block, which meant
          only super-admins could open them. */}
      <Route path="/tickets/:orderId" element={<TicketReceipt />} />
      {/* v149.71 — `ScanPage` asks what is being scanned (the same entry
          as Orders → Scan); `?mode=tickets` is the ticket scanner. */}
      <Route path="/scan" element={<ScanPage />} />
      <Route path="/app/scan" element={<ScanPage />} />
      {/* v147.92 — Host live gate-flow dashboard. Access-controlled
          server-side by the event_scan permission rules (owner + event
          staff + super admin). */}
      <Route path="/app/events/:productId/live" element={<EventLivePage />} />
      {/* v147.14 — catch-all so unknown routes never render a blank page */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppToaster />
          <AppDownloadRibbon />
          <ImpersonationBanner />
          <ErrorBoundary>
            <AppRouter />
          </ErrorBoundary>
          {/* v147.52 — Native force-update gate. Renders NOTHING on
              web builds and stays invisible on native unless Super
              Admin has flipped `force_update` on for the current
              platform AND the installed versionCode is below the
              configured minimum. */}
          <ForceUpdateGate />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
